import { ForwardedRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { ImageResourceProps, RefetchImageHandle } from './ImageResource.types';
import { useSusGuiImages } from '../../services/image/image.service';
import { Container, ImageMessage, ImageWrapper, SolidImageWrapper } from './ImageResource.styles';
import { LabelWrapper, StyledLabel } from '../Input/Input.styles';
import ImageIcon from '@mui/icons-material/Image';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Skeleton } from '@mui/material';

const pad = (num: number, size: number): string => {
  let s = num + '';
  while (s.length < size) s = '0' + s;
  return s;
};

export const padResourceNumbers = (resourceNo: number[], padAmount: number, extension: string = 'jpg') => {
  const joinedNumbers = resourceNo
    .map((value) => {
      return pad(value, padAmount);
    })
    .join('');

  return `${joinedNumbers}.${extension}`;
};

const ImageResource: ForwardRefRenderFunction<RefetchImageHandle, ImageResourceProps> = (
  {
    src,
    alt,
    category = 'clss',
    isLeftPadded = false,
    resourceNo,
    leftPadAmount = 0,
    fileExtension = 'jpg',
    setHasImageLoaded,
  }: ImageResourceProps,
  forwardedRef: ForwardedRef<RefetchImageHandle>
) => {
  if (isLeftPadded) {
    src = padResourceNumbers(resourceNo, leftPadAmount, fileExtension);
  }

  const [imageSourceUrl, setImageSourceUrl] = useState<string>();

  const { data: blob, isLoading, refetch, isError } = useSusGuiImages({ category: category, fileName: src }, true);

  const refetchImage = () => {
    refetch();
  };

  const hasImageLoadingFailed = () => imageSourceUrl === undefined;

  useImperativeHandle(
    forwardedRef,
    () => {
      return {
        refetchImage,
        hasImageLoadingFailed
      };
    },
    []
  );

  useEffect(() => {
    if (blob !== undefined) {
      setImageSourceUrl(URL.createObjectURL(blob));
      setHasImageLoaded(true);
    }
  }, [blob]);

  if (isLoading) {
    const muiTheme = createTheme();
    return (
      <Container>
        <LabelWrapper>
          <StyledLabel>{'Image Preview'}</StyledLabel>
        </LabelWrapper>
        <SolidImageWrapper style={{backgroundColor: 'unset'}}>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Skeleton
              variant='rectangular'
              animation='wave'
              sx={{ width: '128px', height: '128px' }}
              key={'menu-counter-mui-skeleton-pricing'}
            />
          </ThemeProvider>
        </SolidImageWrapper>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <LabelWrapper>
          <StyledLabel>{'Image Preview'}</StyledLabel>
        </LabelWrapper>
        <SolidImageWrapper>
          <ImageIcon sx={{ fontSize: 50, color: '#828282' }} />
          <ImageMessage>
            {'No Image Found'}
          </ImageMessage>
        </SolidImageWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <LabelWrapper>
        <StyledLabel>{'Image Preview'}</StyledLabel>
      </LabelWrapper>
      <ImageWrapper style={{height: 'auto'}}>
        <img src={imageSourceUrl} alt={alt} style={{ objectFit: 'contain', height: 'auto', width: 'auto' }}
             data-testid={`ImageResource-${category}`} />
      </ImageWrapper>
    </Container>
  );
};

export default forwardRef(ImageResource);
